import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { ThemeToggle } from "@exiftrator/shared/components";
import { GlassPanel, OpticButton } from "./OpticPrimitives";
import {
  CtaSectionBlock,
  FaqSectionBlock,
  HeroSectionBlock,
  HowItWorksSectionBlock,
  PricingSectionBlock,
  ProductCarouselSectionBlock,
  ComparisonSectionBlock,
  SpotlightSectionBlock,
  StatisticsSectionBlock,
  TestimonialsSectionBlock,
} from "./OpticLandingSections";
import {
  OPTIC_FOOTER_LINKS,
  OPTIC_HEADER_BRAND,
  OPTIC_HEADER_CTA,
  OPTIC_NAV_LINKS,
  OPTIC_PAGE_SECTIONS,
} from "./landing-data";
import type { OpticPageSection } from "./landing.types";

export default function OpticLanding() {
  const [mounted, setMounted] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleCtaClick = (to: string) => {
    if (to.startsWith("#")) {
      const target = document.querySelector(to);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      return;
    }

    if (to.startsWith("mailto:") || to.startsWith("http://") || to.startsWith("https://")) {
      window.location.href = to;
      return;
    }

    navigate(to);
  };

  const renderSection = (section: OpticPageSection) => {
    switch (section.type) {
      case "hero":
        return <HeroSectionBlock key={section.id} section={section} onCtaClick={handleCtaClick} mounted={mounted} />;
      case "spotlight":
        return <SpotlightSectionBlock key={section.id} section={section} onCtaClick={handleCtaClick} />;
      case "statistics":
        return <StatisticsSectionBlock key={section.id} section={section} onCtaClick={handleCtaClick} />;
      case "how_it_works":
        return <HowItWorksSectionBlock key={section.id} section={section} onCtaClick={handleCtaClick} />;
      case "carousel":
        return <ProductCarouselSectionBlock key={section.id} section={section} onCtaClick={handleCtaClick} />;
      case "comparison":
        return <ComparisonSectionBlock key={section.id} section={section} onCtaClick={handleCtaClick} />;
      case "testimonials":
        return <TestimonialsSectionBlock key={section.id} section={section} onCtaClick={handleCtaClick} />;
      case "pricing":
        return <PricingSectionBlock key={section.id} section={section} onCtaClick={handleCtaClick} />;
      case "faq":
        return <FaqSectionBlock key={section.id} section={section} onCtaClick={handleCtaClick} />;
      case "cta":
        return <CtaSectionBlock key={section.id} section={section} onCtaClick={handleCtaClick} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#f3f6fb] font-sans text-[#0f172a] selection:bg-[#4ade80] selection:text-[#020617] dark:bg-[#050505] dark:text-white">
      <div className="pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(circle_at_15%_10%,rgba(15,23,42,0.08),transparent_45%),radial-gradient(circle_at_85%_0%,rgba(74,222,128,0.15),transparent_40%)] dark:bg-[radial-gradient(circle_at_15%_10%,rgba(148,163,184,0.08),transparent_45%),radial-gradient(circle_at_85%_0%,rgba(74,222,128,0.12),transparent_40%)]" />

      <header className="fixed left-1/2 top-5 z-50 w-full max-w-[1120px] -translate-x-1/2 px-4">
        <GlassPanel className="flex items-center justify-between rounded-full px-5 py-3 sm:px-6">
          <Link to="/" className="font-mono text-xs font-bold tracking-[0.25em] text-[#0f172a] dark:text-white">
            {OPTIC_HEADER_BRAND}
          </Link>

          <nav className="hidden items-center gap-5 md:flex lg:gap-7">
            {OPTIC_NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="font-mono text-[10px] uppercase tracking-[0.17em] text-[#64748b] transition-colors hover:text-[#0f172a] dark:text-[#94a3b8] dark:hover:text-white"
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <ThemeToggle className="text-[#64748b] transition-colors hover:text-[#0f172a] dark:text-[#94a3b8] dark:hover:text-white" />
            <OpticButton onClick={() => handleCtaClick(OPTIC_HEADER_CTA.to)} className="h-8 px-3 text-[10px] sm:h-9 sm:px-4">
              {OPTIC_HEADER_CTA.label}
            </OpticButton>
          </div>
        </GlassPanel>
      </header>

      <main className="space-y-1 pb-24 pt-40">
        {OPTIC_PAGE_SECTIONS.blocks.map((section) => renderSection(section))}
      </main>

      <footer className="mx-auto mt-20 flex w-full max-w-[1200px] flex-col gap-3 border-t border-[#1f2430]/15 px-6 py-10 font-mono text-[11px] uppercase tracking-[0.12em] text-[#64748b] dark:border-white/10 dark:text-[#64748b] sm:flex-row sm:items-center sm:justify-between">
        <p>© {new Date().getFullYear()} Exiftrator</p>
        <div className="flex gap-4">
          {OPTIC_FOOTER_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="transition-colors hover:text-[#0f172a] dark:hover:text-white"
            >
              {link.label}
            </a>
          ))}
        </div>
      </footer>
    </div>
  );
}
